/**
 * Client team invitations — calls to clientInviteRoutes.ts.
 */

import { getApiUrl } from './apiClient';
import { getAuthHeadersWithJson } from './apiHeaders';

export type ClientInviteRole = 'user' | 'companyAdmin';

export const CLIENT_INVITE_ROLES: Array<{ value: ClientInviteRole; label: string; description: string }> = [
  { value: 'user', label: 'Team Member', description: 'Answers questions and follows progress.' },
  { value: 'companyAdmin', label: 'Engagement Lead', description: 'Also invites teammates and manages company settings.' },
];

export interface PendingInvite {
  id: string;
  name: string;
  email: string;
  role: string;
  invitedAt: string | null;
}

export interface InviteResult {
  person: PendingInvite;
  emailSent: boolean;
  emailError?: string;
  resent: boolean;
}

async function call<T>(path: string, accessToken: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(getApiUrl(path), { ...init, headers: getAuthHeadersWithJson(accessToken) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data as T;
}

export async function listPendingInvites(companyId: string, accessToken: string): Promise<PendingInvite[]> {
  const data = await call<{ pending?: PendingInvite[] }>(`/companies/${encodeURIComponent(companyId)}/invites`, accessToken);
  return data.pending ?? [];
}

export function inviteClientUser(
  companyId: string,
  invite: { name: string; email: string; role: ClientInviteRole },
  accessToken: string,
): Promise<InviteResult> {
  return call<InviteResult>(`/companies/${encodeURIComponent(companyId)}/invite`, accessToken, {
    method: 'POST',
    body: JSON.stringify(invite),
  });
}

export function resendClientInvite(companyId: string, userId: string, accessToken: string): Promise<InviteResult> {
  return call<InviteResult>(
    `/companies/${encodeURIComponent(companyId)}/invite/${encodeURIComponent(userId)}/resend`,
    accessToken,
    { method: 'POST' },
  );
}

export function roleLabel(role: string): string {
  return role === 'companyAdmin' || role === 'company_admin' ? 'Engagement Lead' : 'Team Member';
}
